<template>
    <form
        class="base-multiselect"
        :class="{ 'base-multiselect--expanded': isExpanded }"
    >
        <label
            :for="inputID"
            class="base-multiselect__label"
        ><span class="sr-only">Filter: </span>{{ label }}</label>

        <div class="base-multiselect__trigger">
            <ul
                v-if="value.length > 0"
                class="base-multiselect__selected-options"
            >
                <li
                    v-for="(option, index) in value"
                    :key="index"
                    class="base-multiselect__selected-option"
                >
                    <button
                        :title="option?.label"
                        @click.prevent="toggleOption(option)"
                    >
                        <span class="sr-only">{{ t('filter.accessibility.unselect') }}</span>
                        <span class="base-multiselect__selected-option-label">{{ getLabelByValue(option) }}</span>
                        <BaseIcon icon="add" />
                    </button>
                </li>
            </ul>

            <template v-if="isExpanded">
                <button
                    v-if="value.length <= 0"
                    class="base-multiselect__trigger-button"
                    aria-hidden="true"
                    tabindex="-1"
                    @click.prevent
                >
                    {{ innerLabel || 'Select filter' }}

                    <BaseIcon :icon="isExpanded ? 'arrow-up' : 'arrow-down'" />
                </button>

                <button
                    class="base-multiselect__trigger-icon"
                    aria-hidden="true"
                    tabindex="-1"
                    @click.prevent
                >
                    <BaseIcon :icon="isExpanded ? 'arrow-up' : 'arrow-down'" />
                </button>
            </template>
            <template v-if="!isExpanded">
                <button
                    v-if="value.length <= 0"
                    class="base-multiselect__trigger-button"
                    aria-hidden="true"
                    tabindex="-1"
                    @click.prevent="onTrigger"
                >
                    {{ innerLabel || 'Select filter' }}

                    <BaseIcon :icon="isExpanded ? 'arrow-up' : 'arrow-down'" />
                </button>

                <button
                    class="base-multiselect__trigger-icon"
                    aria-hidden="true"
                    tabindex="-1"
                    @click.prevent="onTrigger"
                >
                    <BaseIcon :icon="isExpanded ? 'arrow-up' : 'arrow-down'" />
                </button>
            </template>
        </div>

        <div
            class="base-multiselect__combobox"
            @focusout="onFocusOut"
        >
            <template v-if="searchable">
                <label
                    :for="inputID"
                    class="sr-only"
                >
                    {{ t('filter.search_filter_label') }}
                </label>
                <input
                    :id="inputID"
                    ref="input"
                    v-model="inputModel"
                    type="text"
                    autocomplete="off"
                    role="combobox"
                    :aria-controls="listID"
                    aria-autocomplete="list"
                    :aria-expanded="isExpanded"
                    aria-haspopup="listbox"
                    class="base-multiselect__search"
                    :aria-activedescendant="focussedOption ? `${inputID}-option-${focussedOption}` : false"
                    :placeholder="t('filter.search_placeholder')"
                    @focus="expand"
                >
            </template>

            <ul
                v-if="isExpanded"
                :id="listID"
                class="base-multi-select__options"
                role="listbox"
                :aria-labelledby="inputID"
                aria-live="polite"
            >
                <li
                    v-for="(option, index) in options"
                    v-show="isVisible(option.value)"
                    :id="`${inputID}-option-${index}`"
                    :key="`${inputID}-option-${index}`"
                    role="option"
                    :value-selected="isValueSelected(option.value)"
                    :aria-selected="isValueSelected(option.value)"
                    class="base-multiselect__option"
                >
                    <a
                        ref="buttonoptions"
                        tabindex="-1"
                        @click.prevent="toggleOption(option.value)"
                        @focus="focusOption(option.value)"
                        @blur="blurOption"
                    >
                        <div class="sr-only">{{ isValueSelected(option.value) ? t('filter.accessibility.unselect') : t('filter.accessibility.select') }}</div>
                        {{ option.label }}
                    </a>
                </li>
            </ul>
        </div>
    </form>
</template>

<script setup>
const { t } = useI18n();
const instanceId = useId();

const inputID = computed(() => `filter-${instanceId}`);
const listID = computed(() => `filter-list-${instanceId}`);
</script>

<script>
const KEY_ARROW_DOWN = 40;
const KEY_ARROW_UP = 38;
const KEY_ESCAPE = 27;
const KEY_ENTER = 13;
const KEY_SPACE = 32;

export default {
    props: {
        modelValue: {
            type: [Array, String, Number],
            default: undefined,
        },
        label: {
            type: String,
            default: ''
        },
        innerLabel: {
            type: String,
            default: ''
        },
        options: {
            type: Array,
            default() { return []; }
        },
        searchable: {
            type: Boolean,
            default: true
        }
    },
    emits: ['update:modelValue'],
    data() {
        return {
            inputModel: '',
            isExpanded: false,
            focussedOption: null
        };
    },
    computed: {
        value: {
            get() {
                return this.modelValue;
            },
            set(newValue) {
                this.$emit('update:modelValue', newValue);
            },
        }
    },
    beforeUnmount() {
        this.watchKeys(false);
    },
    methods: {
        isValueSelected(optionValue) {
            return this.value.includes(optionValue);
        },
        isVisible(value) {
            return value?.includes(this.inputModel.toLowerCase());
        },
        expand() {
            this.isExpanded = true;
            this.watchKeys(true);
        },
        collapse() {
            this.isExpanded = false;
            this.focussedOption = null;
            this.inputModel = '';
            this.watchKeys(false);
        },
        onTrigger() {
            this.$refs.input.focus();
        },
        onFocusOut(e) { // Check if focus is within combobox
            if (!e.currentTarget.contains(e.relatedTarget)) {
                this.collapse();
            }
        },
        toggleOption(optionValue) {
            const newModel = [...this.value];
            const index = this.value.indexOf(optionValue);

            // If the option isn't present in the selected items yet, add it
            if (index === -1) {
                newModel.push(optionValue);

            // If it is already selected, remove it
            } else {
                newModel.splice(index, 1);
            }

            this.value = newModel;
        },
        focusOption(option) {
            this.focussedOption = option;
        },
        blurOption() {
            this.focussedOption = null;
        },
        watchKeys(bool) {
            if (bool) {
                window.addEventListener('keydown', this.onKeyDown);
            } else {
                window.removeEventListener('keydown', this.onKeyDown);
            }
        },
        onKeyDown(e) {
            if (e.keyCode === KEY_ARROW_DOWN) { // Arrow down - focus next option
                e.preventDefault();
                if (this.focussedOption === null || this.focussedOption + 1 >= this.options.length) { // Focus first option
                    this.$refs.buttonoptions[0].focus();
                    this.focussedOption = 0;
                } else { // Focus next option
                    this.$refs.buttonoptions[this.focussedOption + 1].focus();
                }
            } else if (e.keyCode === KEY_ARROW_UP) { // Arrow up
                e.preventDefault();
                // focus prev
                if (this.focussedOption === null || this.focussedOption - 1 < 0) { // Focus last option
                    this.$refs.buttonoptions[this.$refs.buttonoptions.length - 1].focus();
                    this.focussedOption = this.$refs.buttonoptions.length - 1;
                } else { // Focus previous option
                    this.$refs.buttonoptions[this.focussedOption - 1].focus();
                }
            } else if (e.keyCode === KEY_SPACE || e.keyCode === KEY_ENTER) {
                if (this.focussedOption !== null) {
                    e.preventDefault();
                    this.toggleOption(this.focussedOption);
                }
            } else if (e.keyCode === KEY_ESCAPE) { // Escape
                this.collapse();
            }
        },
        getLabelByValue(optionValue) {
            const option = this.options.find(
                item => item.value === optionValue
            );

            return option ? option.label : '';
        },
    },
};
</script>

<style lang="less" src="./BaseMultiselect.less" />
